import React from "react";

import type { MessagesWriteErrorFields } from "types/MessagesWriteError";
import { PRODUCT_NAME } from "utilities/productName";
import { TEAMS_SUPPORT_CHANNEL_URL } from "utilities/supportUrl";
import styles from "./MessageWriteErrorBoundary.module.scss";

interface Props {
  fields: MessagesWriteErrorFields;
  /** Clears the boundary's error state so the app subtree re-mounts. */
  reset: () => void;
}

/**
 * Full-page crash screen for a `MessagesWriteError` (ADR 065). Modeled on
 * `ApiHealthCheck`'s full-page card.
 */
export default function MessageWriteErrorScreen({ fields, reset }: Props) {
  const [retrying, setRetrying] = React.useState(false);
  const [retryFailed, setRetryFailed] = React.useState(false);

  const handleRetry = async () => {
    setRetrying(true);
    setRetryFailed(false);
    try {
      await fields.retry();
      // Success: the write landed. Clear the boundary so the app re-mounts
      // against the now-consistent cache.
      reset();
    } catch {
      // Still failing — keep the crash screen up so the unsaved messages stay
      // in memory and the user can try again once connectivity returns.
      setRetrying(false);
      setRetryFailed(true);
    }
  };

  return (
    <div className={styles.messageWriteError}>
      <div className={styles.errorContainer}>
        <h1>{fields.title}</h1>
        <p>{fields.detail}</p>
        <p>
          Your unsaved messages are kept here — keep this tab open and retry in
          a moment.
        </p>
        {retryFailed && (
          <p className={styles.retryFailed}>
            Still unable to save. Please try again in a moment.
          </p>
        )}
        <div className={styles.buttonRow}>
          <button
            className={styles.retryButton}
            onClick={handleRetry}
            disabled={retrying}
          >
            {retrying ? "Retrying…" : "Retry"}
          </button>
        </div>
        <p className={styles.supportHint}>
          If this keeps happening, please contact the {PRODUCT_NAME} team on our{" "}
          <a
            target="_blank"
            rel="noopener noreferrer"
            href={TEAMS_SUPPORT_CHANNEL_URL}
          >
            teams channel
          </a>
          .
        </p>
      </div>
    </div>
  );
}
